'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { DemoEmailRow } from './DemoEmailRow';
import { DemoInbox } from './DemoInbox';
import { scoringEmails } from './demo-data';

const SAFE_EMAILS = scoringEmails.filter((e) => !e.isSpam);
const SPAM_EMAILS = scoringEmails.filter((e) => e.isSpam);

// Timing constants (ms)
const ENTER_DELAY = 600;
const SCORE_DELAY = 1200;
const DIM_DELAY = 1800;
const LOOP_PAUSE = 3000;

interface ScoringDemoProps {
  showReasons?: boolean;
  className?: string;
}

export function ScoringDemo({ showReasons = false, className = '' }: ScoringDemoProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { amount: 0.3 });
  const prefersReducedMotion = useReducedMotion();
  const [currentSpamIndex, setCurrentSpamIndex] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'entering' | 'scoring' | 'dimming'>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const currentSpam = SPAM_EMAILS[currentSpamIndex];

  useEffect(() => {
    return () => {
      timeoutRef.current.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isInView) {
      setPhase('idle');
      timeoutRef.current.forEach(clearTimeout);
      timeoutRef.current = [];
      return;
    }

    // Show static final state for reduced motion users
    if (prefersReducedMotion) {
      setPhase('scoring');
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];

    const t1 = setTimeout(() => setPhase('entering'), 300);
    timers.push(t1);

    const t2 = setTimeout(() => setPhase('scoring'), ENTER_DELAY + 300);
    timers.push(t2);

    const t3 = setTimeout(() => setPhase('dimming'), ENTER_DELAY + SCORE_DELAY + 300);
    timers.push(t3);

    const t4 = setTimeout(
      () => {
        setPhase('idle');
        setCurrentSpamIndex((prev) => (prev + 1) % SPAM_EMAILS.length);
      },
      ENTER_DELAY + SCORE_DELAY + DIM_DELAY + LOOP_PAUSE
    );
    timers.push(t4);

    timeoutRef.current = timers;

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isInView, currentSpamIndex, prefersReducedMotion]);

  const noMotion = prefersReducedMotion ?? false;

  return (
    <div
      ref={ref}
      className={className}
      role="img"
      aria-label="Demo: incoming cold outreach emails are scored and flagged automatically"
    >
      <DemoInbox label="Inbox">
        <AnimatePresence mode="popLayout">
          {phase !== 'idle' && (
            <motion.div
              key={currentSpam.id}
              initial={noMotion ? false : { opacity: 0, y: -40, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={noMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
              transition={
                noMotion ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 25 }
              }
            >
              <DemoEmailRow
                from={currentSpam.from}
                subject={currentSpam.subject}
                time={currentSpam.time}
                score={phase === 'scoring' || phase === 'dimming' ? currentSpam.score : undefined}
                scoreLabel={
                  phase === 'scoring' || phase === 'dimming' ? currentSpam.scoreLabel : undefined
                }
                dimmed={phase === 'dimming'}
              />
              {showReasons && phase === 'scoring' && currentSpam.reasons && (
                <motion.div
                  className="mt-1 ml-11 flex flex-wrap gap-1.5"
                  initial={noMotion ? false : { opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={noMotion ? { duration: 0 } : { delay: 0.3 }}
                >
                  {currentSpam.reasons.map((reason) => (
                    <span
                      key={reason}
                      className="rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] text-red-400/80"
                    >
                      {reason}
                    </span>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {SAFE_EMAILS.map((email) => (
          <DemoEmailRow
            key={email.id}
            from={email.from}
            subject={email.subject}
            time={email.time}
          />
        ))}
      </DemoInbox>
    </div>
  );
}
